import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";
import NexusService from "./NexusService";
import FeatureFlagService from "./FeatureFlagService";

export default class OfficerService extends AbstractService {
	static endpoint = ENDPOINTS.officers;

	static officerCallOffsApi = NexusService.officerCallOffsApi;

	static officerRanksApi = NexusService.officerRanksApi;

	static officerCertificationTypesApi = NexusService.OfficerCertificationTypesApi;

	static search(queryParams, data, getCancelToken) {
		return this.post({
			isV2API: true,
			endpoint: this.endpoint,
			queryParams,
			data,
			getCancelToken,
		});
	}

	static uploadOfficerGovtIdImage({ officerId, fileData, imageType }) {
		const data = new FormData();
		data.append("file", fileData);

		return this.post({
			endpoint: `${this.endpoint}/${officerId}/picture/?imagetype=${imageType}`,
			data,
		});
	}

	static uploadOfficerVerificationDocImg({ officerId, fileData }) {
		const data = new FormData();
		data.append("file", fileData);

		return this.post({
			endpoint: `${this.endpoint}/${officerId}/verificationform`,
			data,
		});
	}

	static getById(id, queryParams, getCancelToken) {
		return this.get({
			endpoint: `${this.endpoint}/${id}`,
			queryParams,
			getCancelToken,
		});
	}

	static updateOfficer(params, contact) {
		const { unassignSuspended, ...data } = params;
		return this.put({
			endpoint: `${this.endpoint}`,
			queryParams: { unassignSuspended },
			data: {
				...data,
				ContactViewModel: {
					...data.ContactViewModel,
					FirstName: contact.FirstName,
					LastName: contact.LastName,
					MobilePhone: contact.MobilePhone,
					StreetAddress1: contact.StreetAddress1,
					City: contact.City,
					StateId: contact.StateId,
					Zipcode: contact.Zipcode,
					IsRankRestricted: data.IsRankRestricted,
				},
			},
		});
	}

	static setOfficerGroup({ id, data }) {
		return this.put({
			endpoint: `${this.endpoint}/${id}/favorite`,
			data,
		});
	}

	static getJobsByOfficerId({ officerId, status, pageNumber, displayCount, sortBy, asc }) {
		return this.get({
			endpoint: `${this.endpoint}/${officerId}/${ENDPOINTS.jobs}`,
			queryParams: {
				status,
				pageNumber,
				displayCount,
				sortBy,
				asc,
			},
		});
	}

	static removeOfficerVerification(officerId, verificationId) {
		return this.del({
			endpoint: `${this.endpoint}/${officerId}/verification/${verificationId}`,
		});
	}

	static getAll(queryParams, getCancelToken) {
		return this.get({
			endpoint: ENDPOINTS.groupedOfficers,
			isV2API: true,
			queryParams,
			getCancelToken,
		});
	}

	static getCallOffs(id) {
		if (FeatureFlagService.isEnabled("use_api_core_endpoints_officer_call_offs")) {
			return this.officerCallOffsApi.lookupOfficerCallOffs({ userId: id }).then(response => {
				const apiResponse = {
					data: response,
				};

				return apiResponse;
			});
		}
		return this.get({
			endpoint: `officercalloff/getCallOffList/${id}`,
		});
	}

	static voidCallOff(data) {
		if (FeatureFlagService.isEnabled("use_api_core_endpoints_officer_call_offs")) {
			return this.officerCallOffsApi.deleteOfficerCallOff({ id: data.Id }).then(response => {
				const apiResponse = {
					data: response,
				};

				return apiResponse;
			});
		}
		return this.del({
			endpoint: "officercalloff/delete",
			data,
		});
	}

	static restoreCallOff(data) {
		if (FeatureFlagService.isEnabled("use_api_core_endpoints_officer_call_offs")) {
			const request = {
				id: data.Id,
				officerCallOffRestorePayload: {},
			};

			if (Object.hasOwn(data, "JobId")) {
				request.officerCallOffRestorePayload.JobId = data.JobId;
			}
			if (Object.hasOwn(data, "UserId")) {
				request.officerCallOffRestorePayload.UserId = data.UserId;
			}
			if (Object.hasOwn(data, "ClientId")) {
				request.officerCallOffRestorePayload.ClientId = data.ClientId;
			}
			if (Object.hasOwn(data, "ClientName")) {
				request.officerCallOffRestorePayload.ClientName = data.ClientName;
			}
			if (Object.hasOwn(data, "StartDate")) {
				request.officerCallOffRestorePayload.StartDate = data.StartDate;
			}
			if (Object.hasOwn(data, "Location")) {
				request.officerCallOffRestorePayload.Location = data.Location;
			}
			if (Object.hasOwn(data, "CallOffDate")) {
				request.officerCallOffRestorePayload.CallOffDate = data.CallOffDate;
			}
			if (Object.hasOwn(data, "Reason")) {
				request.officerCallOffRestorePayload.Reason = data.Reason;
			}
			if (Object.hasOwn(data, "JobEventId")) {
				request.officerCallOffRestorePayload.JobEventId = data.JobEventId;
			}
			if (Object.hasOwn(data, "CallOffType")) {
				request.officerCallOffRestorePayload.CallOffType = data.CallOffType;
			}
			if (Object.hasOwn(data, "CallOffOrigin")) {
				request.officerCallOffRestorePayload.CallOffOrigin = data.CallOffOrigin;
			}

			return this.officerCallOffsApi.restoreOfficerCallOff(request).then(response => {
				const apiResponse = {
					data: response,
				};
				return apiResponse;
			});
		}
		return this.put({
			endpoint: "officercalloff/restore",
			data,
		});
	}

	static getRanks(queryParams) {
		if (queryParams && Object.hasOwn(queryParams, "jurisdictionId")) {
			return this.officerRanksApi
				.getJurisdictionOfficerRanks({ jurisdictionId: queryParams.jurisdictionId })
				.then(response => {
					const apiResponse = {
						data: response.map(({ Id, Description, Weight }) => ({
							Value: Id,
							Text: Description,
							Weight,
						})),
					};

					return apiResponse;
				});
		}

		return this.officerRanksApi.getOfficerRanks().then(response => {
			const apiResponse = {
				data: response.map(({ Id, Description, Weight }) => ({
					Value: Id,
					Text: Description,
					Weight,
				})),
			};

			return apiResponse;
		});
	}

	static getOfficerGroups(id) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/favorite`,
		});
	}

	static getCounts(queryParams) {
		return this.get({
			endpoint: `${this.endpoint}/${ENDPOINTS.counts}`,
			queryParams,
		});
	}

	static updateOfficerApproval(id) {
		return this.put({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static verify(id, data) {
		return this.put({
			endpoint: `${this.endpoint}/${id}/verify`,
			data,
		});
	}

	static sendReminder(id) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/userreminders`,
		});
	}

	static getLastReminder(id) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/userreminders/last`,
		});
	}

	static getJurisdictionHistory(id) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/jurisdictionhistory`,
		});
	}

	static getOfficerCertifications(id) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/certification`,
		});
	}

	static updateOfficerCertifications(id, data) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/certification`,
			data,
		});
	}

	static getCertifications() {
		return this.officerCertificationTypesApi
			.getOfficerCertificationTypes()
			.then(response => {
				const apiResponse = {
					data: response,
				};

				return apiResponse;
			});
	}

	static SendEmailToSupervisor(id) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/SendEmailToSupervisor`,
		});
	}

	static sendOfficerVerification(id) {
		return this.put({
			endpoint: `${this.endpoint}/${id}/activationtokenexpiration`,
		});
	}

	static getOfficerPaymentRestriction(userId) {
		return this.get({
			endpoint: `${this.endpoint}/paymentrestriction?userId=${userId}`,
		});
	}

	static updateOfficerPaymentRestriction(data) {
		return this.post({
			endpoint: `${this.endpoint}/paymentrestriction`,
			data,
		});
	}
}
